import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Timer, Gauge, Loader2, Flag, Crown, ArrowRight } from 'lucide-react';
import TrackMap3D from '@/components/telemetry/TrackMap3D';
import TrackMapModal from '@/components/telemetry/TrackMapModal';
import CountryFlag from '@/components/telemetry/CountryFlag';
import { fmtDate } from '@/lib/utils';

function lapToSec(s) {
  if (!s) return Infinity;
  const m = String(s).match(/(\d+):([\d.]+)/);
  if (!m) return Infinity;
  return parseInt(m[1], 10) * 60 + parseFloat(m[2]);
}

const matchesTrack = (track, keywords) => {
  const t = (track || '').toLowerCase();
  return keywords.some((k) => t.includes(k));
};

const MEDAL = ['text-amber-400', 'text-slate-300', 'text-amber-700'];
const MEDAL_DOT = ['bg-amber-400', 'bg-slate-300', 'bg-amber-700'];

export default function TrackLeaderboard({
  kicker,
  title,
  subtitle,
  icon: Icon,
  trackKey,
  keywords,
  accent,
  accentText,
  borderClass,
  bandClass,
  ambient,
  flag,
  deepLinked = false,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [highlight, setHighlight] = useState(deepLinked);

  useEffect(() => {
    if (!deepLinked) return;
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), 3000);
    return () => clearTimeout(t);
  }, [deepLinked]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      base44.entities.SessionResult.list('-updated_date', 200)
        .then((data) => {
          if (!alive) return;
          const filtered = (data || []).filter((r) => matchesTrack(r.track, keywords) && r.fastest_lap_time);
          const ranked = filtered
            .map((r) => ({ ...r, _sec: lapToSec(r.fastest_lap_time) }))
            .filter((r) => r._sec !== Infinity)
            .sort((a, b) => a._sec - b._sec)
            .slice(0, 10);
          setRows(ranked);
          setLoading(false);
        })
        .catch(() => { if (alive) setLoading(false); });
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [keywords.join(',')]);

  return (
    <div
      id={`track-${trackKey}`}
      className={`relative overflow-hidden border bg-[#0a0b0e] transition-all duration-1000 ${
        highlight
          ? 'border-[#dd1f26] ring-2 ring-red-500/50 shadow-[0_0_20px_rgba(221,31,38,0.35)]'
          : borderClass
      }`}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: ambient }} />

      {/* header */}
      <div className={`relative px-6 py-6 border-b ${borderClass} bg-gradient-to-r ${bandClass}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <TrackMap3D trackKey={trackKey} accent={accent} onClick={() => setShowMap(true)} />
            <div>
              <p className={`${accentText} text-[10px] font-black tracking-[0.3em] mb-1 flex items-center gap-1.5`}>
                <CountryFlag code={flag} size={14} /> {kicker}
              </p>
              <h2 className="text-white font-black text-2xl md:text-3xl uppercase tracking-tight leading-none">{title}</h2>
              <p className="text-white/40 text-xs mt-1.5">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <p className={`font-black text-2xl tabular-nums leading-none ${accentText}`}>{rows.length}</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mt-1">Entries</p>
            </div>
            {rows[0] && (
              <div className={`text-right border-l ${borderClass} pl-5`}>
                <p className={`font-mono font-black text-2xl tabular-nums leading-none ${accentText}`}>{rows[0].fastest_lap_time}</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mt-1">Track Record</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* table */}
      <div className="relative">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: accent }} /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-14 px-6">
            <div className="inline-flex items-center gap-2 border border-[#dd1f26]/40 bg-[#dd1f26]/10 text-[#dd1f26] px-4 py-1.5 mb-5">
              <Crown className="w-4 h-4" />
              <span className="text-[10px] font-black tracking-[0.25em] uppercase">Unclaimed Pole Position</span>
            </div>
            <p className="text-white/50 text-sm font-bold uppercase tracking-wide">Be the first to set a lap time</p>
            <p className="text-white/30 text-xs mt-1.5 max-w-md mx-auto">No driver has laid down a time at {title} yet. This leaderboard is wide open — claim the top step.</p>
            <Link
              to="/bookings"
              className="group inline-flex items-center gap-2 mt-6 border border-[#dd1f26] bg-[#dd1f26]/10 hover:bg-[#dd1f26] text-[#dd1f26] hover:text-white px-5 py-2.5 text-xs font-black tracking-widest uppercase transition-colors"
            >
              Book a Rig to Set This Record
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            <div className="grid grid-cols-12 gap-3 px-6 py-2.5 text-[10px] font-bold tracking-widest uppercase text-white/30">
              <div className="col-span-1">Pos</div>
              <div className="col-span-4">Driver</div>
              <div className="col-span-2">Rig</div>
              <div className="col-span-2 text-right">Lap Time</div>
              <div className="col-span-2 text-right">Top Speed</div>
              <div className="col-span-1 text-right">Date</div>
            </div>
            {rows.map((r, i) => (
              <div key={r.id} className={`grid grid-cols-12 gap-3 px-6 py-3 items-center text-sm ${i < 3 ? '' : ''}`} style={i < 3 ? { background: `${accent}0d` } : undefined}>
                <div className={`col-span-1 font-black text-lg tabular-nums ${MEDAL[i] || 'text-white/40'}`}>{i + 1}</div>
                <div className="col-span-4 text-white font-bold truncate flex items-center gap-2">
                  {i < 3 && <span className={`w-1.5 h-1.5 rounded-full ${MEDAL_DOT[i]}`} />}
                  {r.customer_name || '—'}
                </div>
                <div className="col-span-2 text-white/50 text-xs font-mono uppercase truncate">{r.rig_type || '—'}</div>
                <div className="col-span-2 text-right">
                  <span className={`font-mono font-bold tabular-nums ${accentText}`}>{r.fastest_lap_time}</span>
                </div>
                <div className="col-span-2 text-right text-white/50 text-xs font-mono tabular-nums flex items-center justify-end gap-1">
                  <Gauge className="w-3 h-3" style={{ color: `${accent}66` }} />
                  {r.top_speed_kph ? `${Math.round(r.top_speed_kph)} km/h` : '—'}
                </div>
                <div className="col-span-1 text-right text-white/30 text-[10px] font-mono">
                  {fmtDate(r.session_date) || '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showMap && (
        <TrackMapModal
          trackKey={trackKey}
          title={title}
          kicker={kicker}
          subtitle={subtitle}
          accent={accent}
          accentText={accentText}
          flag={flag}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}